import { Box, Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import { Warning } from "@material-ui/icons";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import InfoIcon from "src/components/Icon/InfoIcon";
import TitleTypography from "src/components/Typography/TitleTypography";
import { THEME_MODE } from "src/config/constants";
import { useUserContext } from "src/context/config/userContext";
import { useUserMonitoringSelector } from "src/context/monitoring/userMonitoringContext";
import { isNumeric, numberWithCommas } from "src/services/utils";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "20px",
    borderRadius: "10px",
    backgroundColor: theme.palette.type === THEME_MODE.DARK ? "#0C4981" : "#7994C1",
  },
  title: {
    color: theme.palette.text.disabled,
  },
  value: {
    fontSize: "28px",
    fontWeight: 500,
  },
}));

export default function LiquidateUserData() {
  const cls = useStyles();
  const { pool } = useParams();
  const selectedAddress = useUserMonitoringSelector((state) => state.selectedAddress);
  const { fetchLiquidateUserData } = useUserMonitoringSelector((state) => state.fetch);

  const selector = useCallback(
    (state) => {
      if (!state.liquidateUserData[pool]) return [];
      return state.liquidateUserData[pool];
    },
    [pool]
  );
  const liquidateUserData = useUserMonitoringSelector(selector);
  const { role } = useUserContext();

  const checkWarning = isNumeric(liquidateUserData.healthFactor) && liquidateUserData.healthFactor < 1;
  const checkData =
    isNumeric(liquidateUserData.liquidationT) &&
    isNumeric(liquidateUserData.ltv) &&
    isNumeric(liquidateUserData.healthFactor);

  useEffect(() => {
    if (selectedAddress) fetchLiquidateUserData(pool, selectedAddress);
  }, [pool, selectedAddress]);

  function Row({ label, title, value }) {
    return (
      <>
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="subtitle2" align="center" className={cls.title}>
            {label}&nbsp;
          </Typography>
          <InfoIcon title={title} iconProps={{ className: cls.title }} />
        </Box>
        <Typography align="center" className={cls.value}>
          {value}
        </Typography>
      </>
    );
  }

  return (
    <Box mt={2}>
      {checkData ? (
        <Paper className={cls.paper}>
          {checkWarning && role === "user" && (
            <Box display="flex" justifyContent="center">
              <Warning />
              <Typography>Warning: your wallet will be liquidated</Typography>
            </Box>
          )}
          <Grid container>
            <Grid xs={12} md={4}>
              <Row
                label="Liquidation Threshold"
                title="The liquidation threshold is the percentage at which a position is defined as undercollateralized. For example, a Liquidation threshold of 80% means that if the value rises above 80% of the collateral, the position is undercollateralized and could be liquidated."
                value={numberWithCommas(liquidateUserData.liquidationT?.toFixed(2), 2)}
              />
            </Grid>
            <Grid xs={12} md={4}>
              <Row
                label="Loan To Value"
                title="The maximum amount that can be borrowed for a specific mortgage is determined by the Loan To Value (LTV) ratio. For example, if the LTV is 75%, borrowers will be allowed to borrow 0.75 ETH worth of corresponding currency for every 1 ETH worth of collateral."
                value={numberWithCommas(liquidateUserData.ltv?.toFixed(2), 2)}
              />
            </Grid>
            <Grid xs={12} md={4}>
              <Row
                label="Health Factor"
                title="When the health factor is lower than 1, the loan may be liquidated to maintain solvency."
                value={
                  liquidateUserData.healthFactor >= 100 ? (
                    "100++"
                  ) : (
                    <>{numberWithCommas(liquidateUserData.healthFactor?.toFixed(2), 2)}</>
                  )
                }
              />
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <Box display="flex" alignItems="center" height="100%" className={cls.root}>
          <TitleTypography textProps={{ variant: "h4" }} title="Click on chart to see data" />
        </Box>
      )}
    </Box>
  );
}
